import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2 } from "lucide-react";

export const PrescriptionChat = ({ prescriptionId, staff = false }) => {
  const { t } = useTranslation();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get(`/prescriptions/${prescriptionId}/messages`)
      .then(({ data }) => { if (active) setMsgs(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [prescriptionId]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/prescriptions/${prescriptionId}/messages`, { text: text.trim() });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const mine = (m) => (staff ? m.sender_role === "staff" : m.sender_role === "patient");

  return (
    <div className="rounded-2xl bg-sand p-3" data-testid={`chat-${prescriptionId}`}>
      <div className="max-h-52 space-y-2 overflow-y-auto pb-2">
        {loading ? (
          <p className="text-xs text-deep-ink/40">…</p>
        ) : msgs.length === 0 ? (
          <p className="text-xs text-deep-ink/40">{t("portal.rx.noMessages")}</p>
        ) : (
          msgs.map((m) => (
            <div key={m.id} className={`flex ${mine(m) ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine(m) ? "bg-deep-ink text-warm-ivory" : "bg-warm-ivory text-deep-ink"}`}>
                <p className="mb-0.5 text-[10px] uppercase tracking-editorial opacity-60">
                  {m.sender_role === "staff" ? t("portal.rx.practice") : (m.sender_name || t("portal.rx.you"))}
                </p>
                {m.text}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-2 flex gap-2">
        <Input data-testid={`chat-input-${prescriptionId}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={t("portal.rx.messagePlaceholder")} className="bg-warm-ivory" />
        <Button data-testid={`chat-send-${prescriptionId}`} onClick={send} disabled={busy} size="icon"
          className="flex-shrink-0 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
